import {
  Box,
  Button,
  Dropdown,
  DropdownOption,
  Modal,
  SearchBar
} from "@modernatx-internal/ui-kit-react";
import React from "react";

import { useCorporateSiteLocalePath } from "@/hooks/useCorporateSiteLocalePath";
import { useCountrySelector } from "@/hooks/useCountrySelector";
import { useExperience } from "@/hooks/useExperience";
import { initializeRouterState } from "@/hooks/useInitializeRouter";
import { useIsPageLoading } from "@/hooks/useIsPageLoading";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { useText } from "@/hooks/useText";
import { CountryCode } from "@/types";
import {
  deleteGeoSelectedCookie,
  deleteHCPConfirmedCookie,
  getGeoSelectedCookieName,
  getHCPConfirmedCookieName
} from "@/utils/getCookieHelpers";

import { RichText } from "./RichText";

const CountrySelectorModalContent: React.FC<
  React.PropsWithChildren<{ buttons?: React.ReactNode }>
> = ({ buttons, children }) => {
  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1
        }}
      >
        {children}
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "end",
          flexGrow: 0,
          pb: 1,
          pt: 4
        }}
      >
        {buttons}
      </Box>
    </React.Fragment>
  );
};

export const CountrySelector: React.FC = () => {
  const localePath = useCorporateSiteLocalePath();
  const {
    allCountryOptions,
    countryOptions,
    globalCountryOptions,
    pathCountry,
    pathLanguage,
    userLanguage
  } = useExperience();
  const { countrySelection, countrySelectionSet, countrySelectorVisible } = useCountrySelector();
  const { getText } = useText();
  const { getLocalizedPath } = useLocalizedPath();
  const { isPageLoading } = useIsPageLoading();
  const [countrySearching, countrySearchingSet] = React.useState(false);
  const [countrySearchValue, countrySearchValueSet] = React.useState("");
  const [languageSelected, languageSelectedSet] = React.useState<DropdownOption | undefined>(
    userLanguage?.iso2 ? { label: userLanguage.native, value: userLanguage?.iso2 } : undefined
  );
  const allCountryOptionsSorted = React.useMemo(
    () => allCountryOptions.sort((prev, next) => prev.name.localeCompare(next.name)),
    [allCountryOptions]
  );
  const allCountryOptionsFiltered = React.useMemo(
    () =>
      allCountryOptionsSorted
        .filter((countryOption) =>
          !countrySearching
            ? countryOption.iso2 !== "GLOBAL"
            : (countryOption.name + " " + countryOption.native)
                .toLowerCase()
                .includes(countrySearchValue.toLowerCase()) && countryOption.iso2 !== "GLOBAL"
        )
        .map((countryOption) => ({
          label: `${countryOption.name} ${countryOption.native !== countryOption.name ? `(${countryOption.native})` : ""}`,
          value: countryOption.iso2
        })),
    [allCountryOptionsSorted, countrySearching, countrySearchValue]
  );
  const pathCountrySpecific = countryOptions.find(
    (countryOption) => countryOption.iso2 === pathCountry?.iso2
  );
  const pathCountryGlobal = globalCountryOptions.find(
    (countryOption) => countryOption.iso2 === pathCountry?.iso2
  );
  const countrySelectionSpecific = countryOptions.find(
    (countryOption) => countryOption.iso2 === countrySelection
  );
  const countrySelectionSpecificSupported = !!countrySelectionSpecific;
  const countrySelectionGlobal = globalCountryOptions.find(
    (countryOption) => countryOption.iso2 === countrySelection
  );
  const countrySelectionGlobalSupported =
    !!countrySelectionGlobal && !countrySelectionSpecificSupported;
  const globalCountryOption = countryOptions.find(
    (countryOption) => countryOption.iso2 === "GLOBAL"
  );
  const countryLanguagePathname = React.useMemo(() => {
    if (countrySelectionSpecific) {
      if (languageSelected) {
        return `/${countrySelectionSpecific.iso2.toLowerCase()}/${languageSelected.value}`;
      }

      return `/${countrySelectionSpecific.iso2.toLowerCase()}`;
    }

    if (countrySelectionGlobalSupported) {
      if (languageSelected) {
        return `/global/${languageSelected.value}`;
      }

      return `/global`;
    }

    if (countrySelection) {
      return `/${countrySelection.toLowerCase()}`;
    }

    return `/${pathCountry?.iso2?.toLowerCase()}/${pathLanguage?.iso2}`;
  }, [
    countrySelection,
    countrySelectionSpecific,
    countrySelectionGlobalSupported,
    languageSelected,
    pathCountry,
    pathLanguage
  ]);
  const languageOptions = countrySelectionSpecific?.languages.length
    ? countrySelectionSpecific.languages
    : countrySelectionGlobalSupported
      ? globalCountryOption?.languages.length
        ? globalCountryOption.languages
        : []
      : [];
  const languageOptionsSorted = [...languageOptions].sort((prev, next) =>
    prev.native.localeCompare(next.native)
  );
  const languageOptionInitial = languageOptions[0];
  const modalContentVisible =
    (!isPageLoading && countrySelectorVisible) || initializeRouterState.isRouterInitializing;

  if (
    !languageOptions.some((languageOption) => languageOption.iso2 === languageSelected?.value) &&
    languageOptionInitial
  ) {
    languageSelectedSet({
      label: languageOptionInitial.native,
      value: languageOptionInitial.iso2
    });
  }

  const handleContinueClick = React.useCallback(() => {
    document.cookie = `${getHCPConfirmedCookieName()}=1; path=/;`;
  }, []);

  const handleAttestBackClick = React.useCallback(() => {
    deleteGeoSelectedCookie();
    deleteHCPConfirmedCookie();
  }, []);

  const handleCountrySelectNextClick = React.useCallback(() => {
    document.cookie = `${getGeoSelectedCookieName()}=${countrySelection}; path=/;`;
  }, [countrySelection]);

  const handleCountrySearchOptionSelect = React.useCallback(
    (option: DropdownOption) => {
      countrySearchingSet(false);
      countrySearchValueSet(String(option.label.trim()));
      countrySelectionSet(String(option.value) as CountryCode);
    },
    [countrySelectionSet]
  );

  const handleCountrySearchClearClick = React.useCallback(() => {
    countrySearchingSet(false);
    countrySearchValueSet("");
    countrySelectionSet(null);
  }, [countrySelectionSet]);

  const handleCountrySearchValueChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      countrySearchingSet(true);
      countrySearchValueSet(e.target.value);
      countrySelectionSet(null);
    },
    [countrySelectionSet]
  );

  return (
    <Modal
      title={
        <Box
          as="span"
          sx={{
            opacity: modalContentVisible ? 1 : 0,
            transition: "transition-color-medium"
          }}
        >
          {getText("ATTESTATION_MODAL_TITLE")}
        </Box>
      }
      isOpen={countrySelectorVisible}
      scrollable={false}
      size="sm"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: [122, "unset"],
          opacity: modalContentVisible ? 1 : 0,
          transition: "transition-color-medium"
        }}
      >
        {!allCountryOptions.length && (
          <CountrySelectorModalContent key={-1}>
            <RichText text={getText("ATTESTATION_MODAL_NOT_SUPPORTED_TEXT")} />
          </CountrySelectorModalContent>
        )}
        {!!allCountryOptions.length && !pathCountry && (
          <CountrySelectorModalContent
            buttons={
              <Button
                disabled={!countrySelection}
                href={getLocalizedPath(`${countryLanguagePathname}/temperature-excursion`, false)}
                onClick={handleCountrySelectNextClick}
              >
                {getText("ATTESTATION_MODAL_NEXT")}
              </Button>
            }
            key={0}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 4
              }}
            >
              <RichText text={getText("ATTESTATION_MODAL_SELECT_COUNTRY_TEXT")} />
              <SearchBar
                dropdownOptions={allCountryOptionsFiltered}
                label={getText("ATTESTATION_COUNTRY_SEARCH_LABEL")}
                onChange={handleCountrySearchValueChange}
                onSelectOption={handleCountrySearchOptionSelect}
                onClickClear={handleCountrySearchClearClick}
                type="select"
                value={countrySearchValue}
              />
            </Box>
            {languageOptions.length > 1 && (
              <Box sx={{ pt: 3 }}>
                <Dropdown
                  dropdownOptions={languageOptionsSorted.map((languageOption) => ({
                    value: String(languageOption.iso2),
                    label: languageOption.native
                  }))}
                  label={getText("LANGUAGE")}
                  onSelectOption={languageSelectedSet}
                  selectedOption={languageSelected}
                />
              </Box>
            )}
          </CountrySelectorModalContent>
        )}
        {pathCountry && !!pathCountrySpecific && (
          <CountrySelectorModalContent
            buttons={
              <>
                <Button
                  onClick={handleAttestBackClick}
                  href={getLocalizedPath("/temperature-excursion", false)}
                  variant="subtle"
                >
                  {getText("ATTESTATION_MODAL_BACK")}
                </Button>
                <Button
                  href={getLocalizedPath(`${countryLanguagePathname}/temperature-excursion`, false)}
                  onClick={handleContinueClick}
                >
                  {getText("ATTESTATION_MODAL_CONTINUE")}
                </Button>
              </>
            }
            key={1}
          >
            <RichText text={getText("ATTESTATION_MODAL_TEXT")} />
          </CountrySelectorModalContent>
        )}
        {pathCountry && !pathCountrySpecific && !!pathCountryGlobal && (
          <CountrySelectorModalContent
            buttons={
              <>
                <Button
                  onClick={handleAttestBackClick}
                  href={getLocalizedPath("/temperature-excursion", false)}
                  variant="subtle"
                >
                  {getText("ATTESTATION_MODAL_BACK")}
                </Button>
                <Button
                  href={getLocalizedPath(`${countryLanguagePathname}/temperature-excursion`, false)}
                  onClick={handleContinueClick}
                >
                  {getText("ATTESTATION_MODAL_CONTINUE")}
                </Button>
              </>
            }
            key={2}
          >
            <RichText text={getText("ATTESTATION_MODAL_TEXT")} />
          </CountrySelectorModalContent>
        )}
        {pathCountry && !pathCountrySpecific && !pathCountryGlobal && (
          <CountrySelectorModalContent
            buttons={
              <Button href={`https://www.modernatx.com${localePath}/contact-moderna`}>
                {getText("ATTESTATION_MODAL_OKAY")}
              </Button>
            }
            key={3}
          >
            <RichText text={getText("ATTESTATION_MODAL_NOT_SUPPORTED_TEXT")} />
          </CountrySelectorModalContent>
        )}
      </Box>
    </Modal>
  );
};
